import React, { useRef } from "react";
import { FiEdit3, FiDelete } from "react-icons/fi";
import { ImShift } from "react-icons/im";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import EntryForm from "../EntryForm";
import PrecedenceForm from "../Precedence";
import apiFetch from "../../api";

export default function DetailedTaskList({ user_id, todos, refresh }) {
  return (
    <Stack spacing={3} w="100%" align="stretch">
      {todos.map((todo) => (
        <TodoHelper key={todo.id} user_id={user_id} todo={todo} fetchTodos={refresh} />
      ))}
    </Stack>
  );
}

function TodoHelper({ user_id, todo, fetchTodos }) {
  return (
    <Box p={0} shadow="sm" w="100%" borderRadius="lg" color="black">
      <Flex minWidth="max-content" alignItems="center" borderRadius="lg" gap="2" bg="white" opacity="0.85" px={2} py={1} flexWrap="wrap">
        <Text fontSize="xs" color="gray.600">{todo.value?.toFixed?.(2)}</Text>
        <Text fontSize="xs" color="gray.500">#{todo.id}</Text>
        <Spacer />
        <Text fontWeight="medium">{todo.task}{todo.blocked ? " (blocked)" : ""}</Text>
        <Spacer />
        <Text fontSize="xs">{todo.precedence}</Text>
        <UpdatePrecedenceTodo user_id={user_id} todo={todo} fetchTodos={fetchTodos} />
        <UpdateTodo user_id={user_id} todo={todo} fetchTodos={fetchTodos} />
        <DeleteTodo user_id={user_id} id={todo.id} fetchTodos={fetchTodos} />
      </Flex>
      {todo.explain ? (
        <Text fontSize="sm" color="gray.600" px={2} py={1} bg="white" opacity="0.9">
          {todo.explain}
        </Text>
      ) : null}
    </Box>
  );
}

function UpdatePrecedenceTodo({ user_id, todo, fetchTodos }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const precedenceInput = useRef(null);

  const updatePrecedenceTodo = async () => {
    const updatedPrecedenceTodo = {
      id: todo.id,
      task: todo.task,
      description: todo.description,
      duration: todo.duration,
      importance: todo.importance,
      anxiety: todo.anxiety,
      repeating: todo.repeating,
      date_due: todo.date_due,
      precedence: precedenceInput.current?.value,
      user_id,
    };
    await apiFetch(`/${user_id}/todo/${todo.id}`, {
      method: "PUT",
      body: JSON.stringify(updatedPrecedenceTodo),
    });
    onClose();
    await fetchTodos();
  };

  return (
    <>
      <IconButton h="1.25rem" size="sm" onClick={onOpen} icon={<ImShift />} aria-label="Edit precedence" />
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent mx={3}>
          <ModalHeader>Edit precedence</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <PrecedenceForm precedenceInput={precedenceInput} precedence_default={todo.precedence} />
          </ModalBody>
          <ModalFooter>
            <Button size="sm" onClick={updatePrecedenceTodo}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

function UpdateTodo({ user_id, todo, fetchTodos }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const taskInput = useRef(null);
  const descriptionInput = useRef(null);
  const durationInput = useRef(null);
  const importanceInput = useRef(null);
  const anxietyInput = useRef(null);
  const repeatingInput = useRef(null);
  const date_dueInput = useRef(null);

  const updateTodo = async () => {
    const updatedTodo = {
      id: todo.id,
      task: taskInput?.current?.value,
      description: descriptionInput.current?.value,
      duration: durationInput.current?.value,
      importance: importanceInput.current?.value,
      anxiety: anxietyInput.current?.value,
      repeating: repeatingInput.current?.value,
      date_due: date_dueInput.current?.value,
      user_id,
    };
    await apiFetch(`/${user_id}/todo/${todo.id}`, {
      method: "PUT",
      body: JSON.stringify(updatedTodo),
    });
    onClose();
    await fetchTodos();
  };

  return (
    <>
      <IconButton h="1.25rem" size="sm" onClick={onOpen} icon={<FiEdit3 />} aria-label="Edit task" />
      <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent mx={3}>
          <ModalHeader>Edit task</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <EntryForm
              taskInput={taskInput}
              descriptionInput={descriptionInput}
              durationInput={durationInput}
              importanceInput={importanceInput}
              anxietyInput={anxietyInput}
              repeatingInput={repeatingInput}
              date_dueInput={date_dueInput}
              task_default={todo.task}
              description_default={todo.description}
              duration_default={todo.duration}
              importance_default={todo.importance}
              anxiety_default={todo.anxiety}
              repeating_default={todo.repeating}
              date_due_default={todo.date_due}
            />
          </ModalBody>
          <ModalFooter>
            <Button size="sm" onClick={updateTodo}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

function DeleteTodo({ user_id, id, fetchTodos }) {
  const deleteTodo = async () => {
    await apiFetch(`/${user_id}/todo/${id}`, { method: "DELETE" });
    await fetchTodos();
  };
  return <IconButton h="1.25rem" size="sm" onClick={deleteTodo} icon={<FiDelete />} aria-label="Delete task" />;
}
